import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import classNames from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CiStar } from "react-icons/ci";
import { FaStar } from "react-icons/fa";
import { LuPlus, LuTrash2, LuX } from "react-icons/lu";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { v4 as uuidv4 } from "uuid";

import { ValidationConstants } from "../../../../../app/constants/ValidationConstants";
import { Button } from "../../../../../components/newUI/button";
import { Divider } from "../../../../../components/newUI/divider";
import { Input } from "../../../../../components/newUI/input";
import { Select, SelectOption } from "../../../../../components/newUI/select";
import { FieldLabel, SectionTitle } from "../../../../../components/newUI/text";
import {
  useEditContactMutation,
  useGetContactTypesQuery,
  useGetPhoneTypesQuery,
} from "../../../../../slices/ContactsSlice";
import { IContactProfile, SelectOptionType } from "../../../../../types/types";
import { isValidEmail } from "../../../../../utils/email";
import { CustomFieldsEditSection } from "../../../../customFields/CustomFieldsEditSection";
import { useCustomFieldValues } from "../../../../customFields/useCustomFieldValues";
import { useDuplicateEmailWarning } from "../../../duplicateEmail/useDuplicateEmailWarning";
import {
  DISPLAY_NAME_MAX,
  PERSON_NAME_PATTERN,
  PERSON_NAME_RULE,
} from "../../../utils/nameRules";
import {
  formatPhoneInput,
  phoneNumbersProblem,
} from "../../../utils/phoneNumber";
import {
  ADDRESS_TYPES,
  DEFAULT_ADDRESS_TYPE,
  DEFAULT_PHONE_TYPE,
  PRIMARY_EMAIL_TOOLTIP_ID,
} from "./options";

type Props = {
  contact: IContactProfile;
  onClose: () => void;
  isOpen: boolean;
};

type EmailDraft = {
  emailAddress: string;
  id: string;
  isPrimaryEmail: boolean;
};

type PhoneDraft = {
  id: string;
  number: string;
  type: SelectOption;
};

type AddressDraft = {
  id: string;
  line1: string;
  city: string;
  region: string;
  postalCode: string;
  addressTypeEnumId: string;
};

type FormData = {
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  socialIdentificationNumber: string;
  contactType: SelectOptionType;
  additionalInfo: string;
};

import {
  formatPersonalId,
  personalIdPattern,
} from "../../../../region/region";
import { useFirmRegion } from "../../../../region/useFirmRegion";

const getErrorMessage = (error: unknown) => {
  const data = (error as FetchBaseQueryError | undefined)?.data;

  if (typeof data !== "string") {
    return null;
  }

  return data.split(":")[1]?.trim() || data;
};

const formatPhoneNumber = (value: string) => formatPhoneInput(value);

const buildEmailDrafts = (contact: IContactProfile): EmailDraft[] => {
  if (contact.emails?.length) {
    return contact.emails.map((email) => ({
      id: email.id || uuidv4(),
      emailAddress: email.emailAddress || "",
      isPrimaryEmail: Boolean(email.isPrimaryEmail),
    }));
  }

  return [
    {
      id: uuidv4(),
      emailAddress: "",
      isPrimaryEmail: false,
    },
  ];
};

const buildPhoneDrafts = (contact: IContactProfile): PhoneDraft[] => {
  if (contact.phoneNumbers?.length) {
    return contact.phoneNumbers.map((phone) => ({
      id: phone.id || uuidv4(),
      number: phone.number || "",
      type: {
        value: phone.phoneNumberTypeEnumId || DEFAULT_PHONE_TYPE.value,
        label:
          phone.phoneNumberTypeEnum?.phoneNumberType ||
          DEFAULT_PHONE_TYPE.label,
      },
    }));
  }

  return [
    {
      id: uuidv4(),
      number: "",
      type: DEFAULT_PHONE_TYPE,
    },
  ];
};

const buildAddressDrafts = (contact: IContactProfile): AddressDraft[] => {
  if (contact.addresses?.length) {
    return contact.addresses.map((address) => ({
      id: address.id || uuidv4(),
      line1: address.line1 || "",
      city: address.city || "",
      region: address.region || "",
      postalCode: address.postalCode || "",
      addressTypeEnumId: address.addressTypeEnumId || DEFAULT_ADDRESS_TYPE,
    }));
  }

  return [
    {
      id: uuidv4(),
      line1: "",
      city: "",
      region: "",
      postalCode: "",
      addressTypeEnumId: DEFAULT_ADDRESS_TYPE,
    },
  ];
};

const iconButtonClassName =
  "h-9 w-9 shrink-0 rounded-[10px] border-[#E7E9F0] text-[#C3C8D4] hover:bg-[#F7F8FA]";

type SelectChangeValue = SelectOption | SelectOption[] | null;

const getSingleSelectOption = <TOption extends SelectOption>(
  value: SelectChangeValue,
): TOption | null => {
  if (Array.isArray(value)) {
    return (value[0] as TOption | undefined) ?? null;
  }

  return (value as TOption | null) ?? null;
};

const mapSelectOption = (option: SelectOptionType): SelectOption => ({
  label: option.label,
  value: option.value,
  caption: option.caption,
  isBold: option.isBold,
  totalCount: option.totalCount,
});

export const EditContactModal: FC<Props> = ({ contact, isOpen, onClose }) => {
  const region = useFirmRegion();
  const { contactNameRule } = ValidationConstants.customerRules;
  const { data: contactTypes = [], isLoading: isLoadingContactTypes } =
    useGetContactTypesQuery();
  const { data: phoneTypes = [], isLoading: isLoadingPhoneTypes } =
    useGetPhoneTypesQuery();

  const [
    editContact,
    { isSuccess, isLoading: isLoadingEdit, error: errorEdit },
  ] = useEditContactMutation();

  const [emailError, setEmailError] = useState("");
  const { confirmEmails, dialog: duplicateEmailDialog } =
    useDuplicateEmailWarning();
  const customFields = useCustomFieldValues(contact.customerId, "individual");
  const [customFieldError, setCustomFieldError] = useState("");
  const [emailsData, setEmailsData] = useState<EmailDraft[]>(() =>
    buildEmailDrafts(contact),
  );
  const [phoneData, setPhoneData] = useState<PhoneDraft[]>(() =>
    buildPhoneDrafts(contact),
  );
  // Phones need 7+ digits (Roadmap 1803); shown after the first Save.
  const [phoneChecked, setPhoneChecked] = useState(false);
  const phoneError = phoneChecked
    ? phoneNumbersProblem(phoneData.map((phone) => phone.number))
    : "";
  const [addressesData, setAddressesData] = useState<AddressDraft[]>(() =>
    buildAddressDrafts(contact),
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      firstName: contact.firstName || "",
      middleName: contact.middleName || "",
      lastName: contact.lastName || "",
      displayName: contact.displayName || "",
      socialIdentificationNumber: contact.socialIdentificationNumber || "",
      contactType: {
        value: contact.contactTypeEnumId || "",
        label: contact.contactType || "",
      },
      additionalInfo: contact.additionalInfo || "",
    },
  });

  const firstName = watch("firstName");
  const middleName = watch("middleName");
  const lastName = watch("lastName");

  const isSubmitting = isLoadingEdit;
  const submitError = getErrorMessage(errorEdit);
  const phoneTypeOptions = useMemo(
    () => phoneTypes.map(mapSelectOption),
    [phoneTypes],
  );

  const selectedContactTypeValue = watch("contactType")?.value;

  const contactTypeButtons = useMemo(() => {
    return contactTypes.map((contactType) => {
      const isSelected = contactType.value === selectedContactTypeValue;

      return {
        ...contactType,
        isSelected,
      };
    });
  }, [contactTypes, selectedContactTypeValue]);

  useEffect(() => {
    const fullName = [firstName, middleName, lastName]
      .filter(Boolean)
      .join(" ");

    setValue("displayName", fullName, { shouldDirty: true });
  }, [firstName, middleName, lastName, setValue]);

  useEffect(() => {
    reset({
      firstName: contact.firstName || "",
      middleName: contact.middleName || "",
      lastName: contact.lastName || "",
      displayName: contact.displayName || "",
      socialIdentificationNumber: contact.socialIdentificationNumber || "",
      contactType: {
        value: contact.contactTypeEnumId || "",
        label: contact.contactType || "",
      },
      additionalInfo: contact.additionalInfo || "",
    });
    setEmailsData(buildEmailDrafts(contact));
    setPhoneData(buildPhoneDrafts(contact));
    setPhoneChecked(false);
    setAddressesData(buildAddressDrafts(contact));
  }, [contact, reset]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Contact updated successfully");
      onClose();
    }
  }, [isSuccess, onClose]);

  useEffect(() => {
    if (errorEdit) {
      toast.error(submitError || "Failed to update contact");
    }
  }, [errorEdit, submitError]);

  const handleAddEmail = () => {
    setEmailsData((currentEmails) => [
      ...currentEmails,
      {
        id: uuidv4(),
        emailAddress: "",
        isPrimaryEmail: false,
      },
    ]);
  };

  const handleDeleteEmail = (index: number) => {
    setEmailsData((currentEmails) => {
      if (currentEmails.length <= 1) {
        return currentEmails;
      }

      const removedEmail = currentEmails[index];
      const nextEmails = currentEmails.filter(
        (_, emailIndex) => emailIndex !== index,
      );

      if (!removedEmail?.isPrimaryEmail || nextEmails.length === 0) {
        return nextEmails;
      }

      return nextEmails.map((email, emailIndex) => ({
        ...email,
        isPrimaryEmail: emailIndex === 0,
      }));
    });
  };

  const handleAddPhone = () => {
    setPhoneData((currentPhones) => [
      ...currentPhones,
      {
        id: uuidv4(),
        number: "",
        type: DEFAULT_PHONE_TYPE,
      },
    ]);
  };

  const handleDeletePhone = (index: number) => {
    setPhoneData((currentPhones) =>
      currentPhones.length > 1
        ? currentPhones.filter((_, phoneIndex) => phoneIndex !== index)
        : currentPhones,
    );
  };

  const handleAddAddress = () => {
    setAddressesData((currentAddresses) => [
      ...currentAddresses,
      {
        id: uuidv4(),
        line1: "",
        city: "",
        region: "",
        postalCode: "",
        addressTypeEnumId: DEFAULT_ADDRESS_TYPE,
      },
    ]);
  };

  const handleDeleteAddress = (index: number) => {
    setAddressesData((currentAddresses) =>
      currentAddresses.length > 1
        ? currentAddresses.filter((_, addressIndex) => addressIndex !== index)
        : currentAddresses,
    );
  };

  const handleChangePrimaryEmail = (emailId: string) => {
    const targetEmail = emailsData.find((email) => email.id === emailId);

    if (
      !targetEmail ||
      targetEmail.isPrimaryEmail ||
      !targetEmail.emailAddress.trim()
    ) {
      return;
    }

    setEmailsData((currentEmails) =>
      currentEmails.map((currentEmail) => ({
        ...currentEmail,
        isPrimaryEmail: currentEmail.id === emailId,
      })),
    );
  };

  const handleEmailChange = (index: number, value: string) => {
    const isEmpty = value.trim() === "";

    setEmailError(isEmpty || isValidEmail(value) ? "" : "Invalid email");

    setEmailsData((currentEmails) =>
      currentEmails.map((email, emailIndex) =>
        emailIndex === index ? { ...email, emailAddress: value } : email,
      ),
    );
  };

  const onSubmit = async (data: FormData) => {
    if (isLoadingEdit) {
      return;
    }

    if (phoneNumbersProblem(phoneData.map((phone) => phone.number))) {
      setPhoneChecked(true);
      return;
    }

    // Roadmap 1784: warn (never block) when a NEW email of this client is
    // already used by another client of the firm.
    const proceed = await confirmEmails(
      emailsData.map((email) => email.emailAddress),
      {
        before: (contact.emails ?? []).map((email) => email.emailAddress),
        exceptCustomerId: contact.customerId,
      },
    );
    if (!proceed) {
      return;
    }

    const customFieldProblem = await customFields.save();
    setCustomFieldError(customFieldProblem || "");
    if (customFieldProblem !== null) {
      return;
    }

    editContact({
      ...contact,
      contactId: contact.contactId || "",
      customerId: contact.customerId || "",
      locationId: contact.locationId || "",
      firstName: data.firstName || "",
      middleName: data.middleName || "",
      lastName: data.lastName || "",
      displayName: data.displayName || "",
      socialIdentificationNumber: data.socialIdentificationNumber || "",
      emails: emailsData.map((email) => ({
        emailAddress: email.emailAddress,
        isPrimaryEmail: email.isPrimaryEmail,
      })),
      phoneNumbers: phoneData
        .filter((phone) => phone.number !== "")
        .map((phone) => ({
          number: phone.number,
          phoneNumberTypeEnumId: phone.type.value,
        })),
      contactTypeEnumId: data.contactType.value,
      addresses: addressesData.map((address) => ({
        line1: address.line1 || "",
        city: address.city || "",
        region: address.region || "",
        postalCode: address.postalCode || "",
        addressTypeEnumId: address.addressTypeEnumId,
      })),
      additionalInfo: data.additionalInfo || "",
      phoneNumberTypeEnumId:
        phoneData[0]?.type.value || DEFAULT_PHONE_TYPE.value,
    });
  };

  return (
    <div
      className={classNames(
        "modal !z-[4]",
        isOpen ? "modal-open" : "modal-close",
      )}>
      <div
        className={
          "modal-box flex max-h-[calc(100vh-5em)] w-full max-w-[620px] flex-col overflow-hidden rounded-xl bg-white p-0"
        }>
        <div
          className={
            "grid grid-cols-[1fr_max-content] border-b border-[#EFF0F4] px-7 py-4"
          }>
          <p className={"text-lg font-bold text-[#1F2433]"}>Contact Info</p>
          <Button
            type={"button"}
            variant={"ghost"}
            size={"sm"}
            iconOnly
            leftIcon={<LuX size={18} />}
            className={"h-8 w-8 text-[#6B7185] hover:bg-[#F1F2F5]"}
            aria-label={"Close contact modal"}
            onClick={onClose}
          />
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className={"flex min-h-0 flex-1 flex-col"}>
          <div className={"min-h-0 flex-1 overflow-y-auto px-7 py-6"}>
            {submitError && (
              <div
                className={
                  "mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-semibold text-error"
                }>
                {submitError}
              </div>
            )}

            <SectionTitle size={"sm"}>Basic Info</SectionTitle>
            <div className={"mb-4 grid grid-cols-[1fr_0.65fr_1fr] gap-4"}>
              <div>
                <FieldLabel label={"First Name"} size={"sm"} />
                <Input
                  placeholder={"First name"}
                  error={!!errors.firstName}
                  {...register("firstName", {
                    ...contactNameRule.valueLength,
                    pattern: {
                      value: PERSON_NAME_PATTERN,
                      message: PERSON_NAME_RULE,
                    },
                    required: "This field is required",
                    maxLength: {
                      value: 20,
                      message:
                        "First name shouldn't be greater than 20 characters",
                    },
                  })}
                />
                {errors.firstName && (
                  <div className={"pt-2 text-xs font-bold text-error"}>
                    {errors.firstName.message}
                  </div>
                )}
              </div>

              <div>
                <FieldLabel label={"Middle"} size={"sm"} />
                <Input
                  placeholder={"Middle"}
                  error={!!errors.middleName}
                  {...register("middleName", {
                    ...contactNameRule.valueLength,
                    pattern: {
                      value: PERSON_NAME_PATTERN,
                      message: PERSON_NAME_RULE,
                    },
                    maxLength: {
                      value: 20,
                      message:
                        "Middle name shouldn't be greater than 20 characters",
                    },
                  })}
                />
                {errors.middleName && (
                  <div className={"pt-2 text-xs font-bold text-error"}>
                    {errors.middleName.message}
                  </div>
                )}
              </div>

              <div>
                <FieldLabel label={"Last Name"} size={"sm"} />
                <Input
                  placeholder={"Last name"}
                  error={!!errors.lastName}
                  {...register("lastName", {
                    ...contactNameRule.valueLength,
                    pattern: {
                      value: PERSON_NAME_PATTERN,
                      message: PERSON_NAME_RULE,
                    },
                    required: "This field is required",
                    maxLength: {
                      value: 20,
                      message:
                        "Last name shouldn't be greater than 20 characters",
                    },
                  })}
                />
                {errors.lastName && (
                  <div className={"pt-2 text-xs font-bold text-error"}>
                    {errors.lastName.message}
                  </div>
                )}
              </div>
            </div>

            <div className={"mb-4"}>
              <FieldLabel label={"Display Name"} size={"sm"} />
              <Input
                placeholder={"Display name"}
                error={!!errors.displayName}
                {...register("displayName", {
                  pattern: {
                    value: PERSON_NAME_PATTERN,
                    message: PERSON_NAME_RULE,
                  },
                  maxLength: {
                    value: DISPLAY_NAME_MAX,
                    message: `Must not be more than ${DISPLAY_NAME_MAX} characters`,
                  },
                })}
              />
              {errors.displayName && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {errors.displayName.message}
                </div>
              )}
            </div>

            <div>
              <FieldLabel label={region.personalId.label} size={"sm"} />
              <Input
                placeholder={region.personalId.placeholder}
                error={!!errors.socialIdentificationNumber}
                {...register("socialIdentificationNumber", {
                  pattern: {
                    value: personalIdPattern(region.code),
                    message: region.personalId.invalid,
                  },
                  onChange: (event) => {
                    setValue(
                      "socialIdentificationNumber",
                      formatPersonalId(region.code, event.target.value),
                    );
                  },
                })}
              />
              {errors.socialIdentificationNumber && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {errors.socialIdentificationNumber.message}
                </div>
              )}
            </div>

            <Divider />

            <SectionTitle size={"sm"}>Contact Details</SectionTitle>
            <div
              className={
                "mb-4 rounded-xl border border-[#E4E7EE] bg-[#F3F4F7] p-4"
              }>
              <FieldLabel label={"Email"} size={"sm"} />
              <div className={"grid gap-2"}>
                {emailsData.map((email, index) => {
                  const isPrimaryActionDisabled =
                    !email.emailAddress.trim() || isLoadingEdit;
                  const primaryActionTooltip = email.isPrimaryEmail
                    ? "Already primary email"
                    : email.emailAddress.trim()
                    ? "Make primary email"
                    : "Enter email before making it primary";

                  return (
                    <div key={email.id} className={"flex items-start gap-2"}>
                      <div className={"flex h-10 w-6 shrink-0 items-center"}>
                        {index === 0 ? (
                          <Button
                            type={"button"}
                            variant={"primary"}
                            size={"xs"}
                            iconOnly
                            leftIcon={<LuPlus size={16} strokeWidth={3} />}
                            //className={addButtonClassName}
                            className={"shrink-0"}
                            aria-label={"Add email"}
                            onClick={handleAddEmail}
                          />
                        ) : (
                          <span className={"h-6 w-6"} />
                        )}
                      </div>
                      <div className={"flex min-w-0 flex-1 items-center gap-2"}>
                        <Input
                          placeholder={"Email"}
                          value={email.emailAddress}
                          onChange={(event) =>
                            handleEmailChange(index, event.target.value)
                          }
                        />
                        <Button
                          iconOnly
                          size={"md"}
                          variant={
                            email.isPrimaryEmail
                              ? "success-secondary"
                              : "outline"
                          }
                          leftIcon={
                            email.isPrimaryEmail ? (
                              <FaStar size={16} />
                            ) : (
                              <CiStar size={18} />
                            )
                          }
                          disabled={isPrimaryActionDisabled}
                          onClick={() => handleChangePrimaryEmail(email.id)}
                          type={"button"}
                          data-tooltip-id={PRIMARY_EMAIL_TOOLTIP_ID}
                          data-tooltip-content={primaryActionTooltip}
                          className={"shrink-0"}
                        />
                        <Button
                          type={"button"}
                          variant={"outline"}
                          size={"md"}
                          iconOnly
                          leftIcon={<LuTrash2 size={17} />}
                          className={classNames(
                            iconButtonClassName,
                            emailsData.length > 1
                              ? "text-[#D0473F] hover:border-[#E7E9F0] hover:bg-[#FDF1F1]"
                              : "cursor-not-allowed text-[#C3C8D4]",
                          )}
                          disabled={emailsData.length <= 1}
                          aria-label={"Delete email"}
                          onClick={() => handleDeleteEmail(index)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {emailError && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {emailError}
                </div>
              )}

              <div className={"mt-4"}>
                <FieldLabel label={"Phone Number"} size={"sm"} />
                <div className={"grid gap-2.5"}>
                  {phoneData.map((phone, index) => (
                    <div key={phone.id} className={"flex items-start gap-2"}>
                      <div className={"flex h-10 w-6 shrink-0 items-center"}>
                        {index === 0 ? (
                          <Button
                            type={"button"}
                            variant={"primary"}
                            size={"xs"}
                            iconOnly
                            leftIcon={<LuPlus size={16} strokeWidth={3} />}
                            className={"shrink-0"}
                            aria-label={"Add phone number"}
                            onClick={handleAddPhone}
                          />
                        ) : (
                          <span className={"h-6 w-6"} />
                        )}
                      </div>
                      <div className={"flex min-w-0 flex-1 items-center gap-2"}>
                        <div className={"w-[130px] shrink-0"}>
                          <Select
                            options={phoneTypeOptions}
                            value={phone.type}
                            placeholder={"Select type"}
                            loading={isLoadingPhoneTypes}
                            size={"md"}
                            dropMenuVariant={"portal"}
                            styleConfig={{
                              fullWidth: true,
                              menuWidth: 180,
                              maxMenuHeight: 220,
                              controlClassName: "min-h-0",
                            }}
                            onChange={(value) => {
                              const newType = getSingleSelectOption(value);

                              if (!newType) {
                                return;
                              }

                              setPhoneData((currentPhones) =>
                                currentPhones.map((currentPhone, phoneIndex) =>
                                  phoneIndex === index
                                    ? { ...currentPhone, type: newType }
                                    : currentPhone,
                                ),
                              );
                            }}
                          />
                        </div>
                        <Input
                          placeholder={"Phone number"}
                          value={phone.number}
                          onChange={(event) => {
                            const formattedPhone = formatPhoneNumber(
                              event.target.value,
                            );
                            setPhoneData((currentPhones) =>
                              currentPhones.map((currentPhone, phoneIndex) =>
                                phoneIndex === index
                                  ? { ...currentPhone, number: formattedPhone }
                                  : currentPhone,
                              ),
                            );
                          }}
                        />
                        <Button
                          type={"button"}
                          variant={"outline"}
                          size={"md"}
                          iconOnly
                          leftIcon={<LuTrash2 size={17} />}
                          className={classNames(
                            iconButtonClassName,
                            phoneData.length > 1
                              ? "text-[#D0473F] hover:border-[#E7E9F0] hover:bg-[#FDF1F1]"
                              : "cursor-not-allowed text-[#C3C8D4]",
                          )}
                          disabled={phoneData.length <= 1}
                          aria-label={"Delete phone number"}
                          onClick={() => handleDeletePhone(index)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {phoneError && (
                  <div className={"pt-2 text-xs font-bold text-error"}>
                    {phoneError}
                  </div>
                )}
              </div>
            </div>

            <SectionTitle size={"sm"}>Address</SectionTitle>
            <div className={"grid gap-4"}>
              {addressesData.map((address, index) => (
                <div key={address.id} className={"flex items-start gap-2"}>
                  <div className={"flex h-10 w-6 shrink-0 items-center"}>
                    {index === 0 ? (
                      <Button
                        type={"button"}
                        variant={"primary"}
                        size={"xs"}
                        iconOnly
                        leftIcon={<LuPlus size={16} strokeWidth={3} />}
                        className={"shrink-0"}
                        aria-label={"Add address"}
                        onClick={handleAddAddress}
                      />
                    ) : (
                      <span className={"h-6 w-6"} />
                    )}
                  </div>
                  <div className={"grid min-w-0 flex-1 gap-3"}>
                    <div className={"flex items-center gap-2"}>
                      <Select
                        value={
                          ADDRESS_TYPES.find(
                            (item) => item.value === address.addressTypeEnumId,
                          ) || ADDRESS_TYPES[0]
                        }
                        options={ADDRESS_TYPES}
                        placeholder={"Select address type"}
                        size={"md"}
                        dropMenuVariant={"portal"}
                        styleConfig={{
                          fullWidth: true,
                          menuWidth: 240,
                          controlClassName: "min-h-0 ",
                        }}
                        onChange={(value) => {
                          const option = getSingleSelectOption(value);

                          if (!option) {
                            return;
                          }

                          setAddressesData((currentAddresses) =>
                            currentAddresses.map(
                              (currentAddress, addressIndex) =>
                                addressIndex === index
                                  ? {
                                      ...currentAddress,
                                      addressTypeEnumId: option.value,
                                    }
                                  : currentAddress,
                            ),
                          );
                        }}
                      />
                      <Button
                        type={"button"}
                        variant={"outline"}
                        size={"md"}
                        iconOnly
                        leftIcon={<LuTrash2 size={17} />}
                        className={classNames(
                          iconButtonClassName,
                          addressesData.length > 1
                            ? "text-[#D0473F] hover:border-[#E7E9F0] hover:bg-[#FDF1F1]"
                            : "cursor-not-allowed text-[#C3C8D4]",
                        )}
                        disabled={addressesData.length <= 1}
                        aria-label={"Delete address"}
                        onClick={() => handleDeleteAddress(index)}
                      />
                    </div>
                    <Input
                      placeholder={"Street address"}
                      value={address.line1}
                      onChange={(event) =>
                        setAddressesData((currentAddresses) =>
                          currentAddresses.map((currentAddress, addressIndex) =>
                            addressIndex === index
                              ? {
                                  ...currentAddress,
                                  line1: event.target.value,
                                }
                              : currentAddress,
                          ),
                        )
                      }
                    />
                    <div className={"grid grid-cols-2 gap-2"}>
                      <Input
                        placeholder={region.cityLabel}
                        value={address.city}
                        onChange={(event) =>
                          setAddressesData((currentAddresses) =>
                            currentAddresses.map(
                              (currentAddress, addressIndex) =>
                                addressIndex === index
                                  ? {
                                      ...currentAddress,
                                      city: event.target.value,
                                    }
                                  : currentAddress,
                            ),
                          )
                        }
                      />
                      <Input
                        placeholder={region.regionLabel}
                        value={address.region}
                        onChange={(event) =>
                          setAddressesData((currentAddresses) =>
                            currentAddresses.map(
                              (currentAddress, addressIndex) =>
                                addressIndex === index
                                  ? {
                                      ...currentAddress,
                                      region: event.target.value,
                                    }
                                  : currentAddress,
                            ),
                          )
                        }
                      />
                    </div>
                    <Input
                      placeholder={region.postalLabel}
                      value={address.postalCode}
                      onChange={(event) =>
                        setAddressesData((currentAddresses) =>
                          currentAddresses.map((currentAddress, addressIndex) =>
                            addressIndex === index
                              ? {
                                  ...currentAddress,
                                  postalCode: event.target.value,
                                }
                              : currentAddress,
                          ),
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            <Divider />

            <CustomFieldsEditSection
              entityType={"individual"}
              values={customFields.values}
              onChange={customFields.setValue}
              showErrors={customFields.showErrors}
            />
            {customFieldError && (
              <div className={"mt-2 text-sm font-semibold text-error"}>
                {customFieldError}
              </div>
            )}

            <Divider />

            <Controller
              control={control}
              name={"contactType"}
              render={({ field }) => (
                <div className={"mb-5"}>
                  <SectionTitle size={"sm"}>Contact Type</SectionTitle>
                  <div className={"flex flex-wrap gap-2"}>
                    {contactTypeButtons.map((contactType) => (
                      <Button
                        key={contactType.value}
                        type={"button"}
                        variant={"ghost"}
                        size={"sm"}
                        autoWidth
                        className={classNames(
                          "h-auto border px-3 py-2 text-sm font-semibold",
                          contactType.isSelected
                            ? "border-[#6B4EFF] bg-[#F7F5FF] text-[#6B4EFF]"
                            : "border-[#DCE2EA] bg-white text-[#3A4256] hover:border-[#C5CCDA]",
                        )}
                        disabled={isLoadingContactTypes}
                        onClick={() => field.onChange(contactType)}>
                        {contactType.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            />
            <div>
              <SectionTitle size={"sm"}>Additional Info</SectionTitle>
              <Input
                as={"textarea"}
                placeholder={"Add any notes about this contact..."}
                rows={4}
                className={"resize-y"}
                {...register("additionalInfo")}
              />
            </div>
          </div>

          <div
            className={
              "flex shrink-0 justify-end gap-2 border-t border-[#EFF0F4] bg-white px-7 py-4"
            }>
            <Button
              type={"button"}
              variant={"outline"}
              size={"md"}
              width={100}
              disabled={isSubmitting}
              onClick={onClose}>
              Cancel
            </Button>
            <Button
              type={"submit"}
              variant={"primary"}
              size={"md"}
              width={100}
              loading={isLoadingEdit}
              disabled={isSubmitting || Boolean(emailError)}>
              Save
            </Button>
          </div>
        </form>
        <ReactTooltip
          id={PRIMARY_EMAIL_TOOLTIP_ID}
          place={"bottom"}
          className={"!rounded-[8px] !p-2 !text-sm"}
        />
      </div>
      {duplicateEmailDialog}
    </div>
  );
};
